import React from "react";

export interface FeedbackMessage {
  feedback?: {
    content?: string;
    rating?: string;
  };
}

/**
 * メッセージ配列から平均星評価を計算する（未評価は除外）
 */
export const calcAverageStar = (messages: FeedbackMessage[] | undefined): number | null => {
  if (!messages || messages.length === 0) return null;
  const rated = messages.filter((m) => m.feedback?.content && /^[1-5]$/.test(m.feedback.content));
  if (rated.length === 0) return null;
  const sum = rated.reduce((acc, m) => acc + Number(m.feedback!.content!), 0);
  return sum / rated.length;
};

/**
 * メッセージ配列からシンプルモード（GOOD/BAD）の集約結果を返す
 * @returns "like" | "dislike" | "mixed" | "unrated"
 */
export const calcSimpleFeedbackSummary = (
  messages: FeedbackMessage[] | undefined
): "like" | "dislike" | "mixed" | "unrated" => {
  if (!messages || messages.length === 0) return "unrated";
  const ratings = messages.map((m) => m.feedback?.rating).filter((r): r is string => !!r);
  if (ratings.length === 0) return "unrated";
  const hasLike = ratings.some((r) => r === "like");
  const hasDislike = ratings.some((r) => r !== "like");
  if (hasLike && hasDislike) return "mixed";
  if (hasLike) return "like";
  return "dislike";
};

/**
 * 小数点対応の平均星評価表示コンポーネント
 * 小数部分を黄色の星で部分満たし表示する
 * 例: 平均3.7 → 星3つと、4つ目が70%塗りつぶされた星を表示
 *
 * @param average 平均星評価（0～5の小数）
 */
export const AverageStarDisplay: React.FC<{ average: number }> = ({ average }) => {
  const pct = Math.min((average / 5) * 100, 100);
  return (
    <span className="avg-star-wrapper">
      <span className="avg-star-track">
        {/* 背景: 空の星 */}
        <span className="avg-star-row avg-star-empty">
          {[1, 2, 3, 4, 5].map((n) => (
            <span key={n} className="material-symbols-rounded avg-star-icon">
              star
            </span>
          ))}
        </span>
        {/* 前景: 黄色の塗りつぶし星（幅でクリップ） */}
        <span className="avg-star-row avg-star-filled" style={{ width: `${pct}%` }}>
          {[1, 2, 3, 4, 5].map((n) => (
            <span key={n} className="material-symbols-rounded avg-star-icon">
              star
            </span>
          ))}
        </span>
      </span>
      <span className="avg-star-value">({average.toFixed(1)})</span>
    </span>
  );
};
